"use client";

import React, { useState, useMemo } from "react";
import { Calculator, Ruler, Scale, Activity, ArrowRight, Info, RotateCcw, FileText, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import DataTable, { DataTableColumn } from "@/components/common/DataTable";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface ReferenceMeshRow {
  mesh: number;
  wireMm: number;
  apertureMm: number;
  micron: number;
  openArea: number;
  weightKg: number;
}
const MATERIAL_DENSITIES: Record<string, {
  density: number;
}> = {
  ss304: {
    density: 7.93
  },
  ss316: {
    density: 7.98
  },
  inconel600: {
    density: 8.47
  },
  titanium: {
    density: 4.51
  },
  brass: {
    density: 8.44
  }
};
const REFERENCE_MESH_DATA: ReferenceMeshRow[] = [{
  mesh: 20,
  wireMm: 0.40,
  apertureMm: 0.87,
  micron: 870,
  openArea: 46.9,
  weightKg: 1.60
}, {
  mesh: 40,
  wireMm: 0.25,
  apertureMm: 0.385,
  micron: 385,
  openArea: 36.8,
  weightKg: 1.25
}, {
  mesh: 60,
  wireMm: 0.19,
  apertureMm: 0.233,
  micron: 233,
  openArea: 30.4,
  weightKg: 1.08
}, {
  mesh: 80,
  wireMm: 0.12,
  apertureMm: 0.198,
  micron: 198,
  openArea: 38.8,
  weightKg: 0.58
}, {
  mesh: 100,
  wireMm: 0.10,
  apertureMm: 0.154,
  micron: 154,
  openArea: 36.8,
  weightKg: 0.50
}, {
  mesh: 200,
  wireMm: 0.05,
  apertureMm: 0.077,
  micron: 77,
  openArea: 36.8,
  weightKg: 0.25
}, {
  mesh: 325,
  wireMm: 0.035,
  apertureMm: 0.043,
  micron: 43,
  openArea: 30.4,
  weightKg: 0.20
}, {
  mesh: 400,
  wireMm: 0.030,
  apertureMm: 0.0335,
  micron: 33.5,
  openArea: 27.8,
  weightKg: 0.18
}];
export default function Calculations() {
  const {
    lang
  } = useLanguage();

  // Tab 1: Open Area & Mesh Geometry
  const [calcMode, setCalcMode] = useState<"from_mesh" | "from_aperture">("from_mesh");
  const [meshInput, setMeshInput] = useState<number>(80);
  const [wireMmInput, setWireMmInput] = useState<number>(0.12);
  const [apertureInput, setApertureInput] = useState<number>(0.198);

  // Tab 2: Weight Calculation
  const [weightMesh, setWeightMesh] = useState<number>(100);
  const [weightWireMm, setWeightWireMm] = useState<number>(0.10);
  const [materialKey, setMaterialKey] = useState<string>("ss316");
  const [sheetLength, setSheetLength] = useState<number>(1.0);
  const [sheetWidth, setSheetWidth] = useState<number>(1.0);

  // Tab 3: Micron & Flow Parameter Estimator
  const [micronInput, setMicronInput] = useState<number>(150);
  const [flowRate, setFlowRate] = useState<number>(2.5); // m3/h
  const [filterArea, setFilterArea] = useState<number>(0.25); // m2

  const materialNames: Record<string, string> = {
    ss304: pick(lang, {
      en: "Stainless Steel 304 (7.93 g/cm³)",
      zh: "304 不锈钢 (7.93 g/cm³)",
      es: "Acero inoxidable 304 (7.93 g/cm³)",
      de: "Edelstahl 304 (7,93 g/cm³)",
      fr: "Acier inoxydable 304 (7,93 g/cm³)",
      pt: "Aço inoxidável 304 (7,93 g/cm³)"
    }),
    ss316: pick(lang, {
      en: "Stainless Steel 316L (7.98 g/cm³)",
      zh: "316L 不锈钢 (7.98 g/cm³)",
      es: "Acero inoxidable 316L (7.98 g/cm³)",
      de: "Edelstahl 316L (7,98 g/cm³)",
      fr: "Acier inoxydable 316L (7,98 g/cm³)",
      pt: "Aço inoxidável 316L (7,98 g/cm³)"
    }),
    inconel600: pick(lang, {
      en: "Inconel 600 (8.47 g/cm³)",
      zh: "Inconel 600 (8.47 g/cm³)",
      es: "Inconel 600 (8.47 g/cm³)",
      de: "Inconel 600 (8,47 g/cm³)",
      fr: "Inconel 600 (8,47 g/cm³)",
      pt: "Inconel 600 (8,47 g/cm³)"
    }),
    titanium: pick(lang, {
      en: "Titanium Grade 2 (4.51 g/cm³)",
      zh: "2级钛合金 (4.51 g/cm³)",
      es: "Titanio Grado 2 (4.51 g/cm³)",
      de: "Titan Grad 2 (4,51 g/cm³)",
      fr: "Titane Grade 2 (4,51 g/cm³)",
      pt: "Titânio Grau 2 (4,51 g/cm³)"
    }),
    brass: pick(lang, {
      en: "Brass CuZn37 (8.44 g/cm³)",
      zh: "黄铜 CuZn37 (8.44 g/cm³)",
      es: "Latón CuZn37 (8.44 g/cm³)",
      de: "Messing CuZn37 (8,44 g/cm³)",
      fr: "Laiton CuZn37 (8,44 g/cm³)",
      pt: "Latão CuZn37 (8,44 g/cm³)"
    })
  };
  const copy = {
    badge: pick(lang, {
      en: "Industrial Engineering Suite",
      zh: "工业工程计算套件",
      es: "Paquete de ingeniería industrial",
      de: "Industrie-Ingenieurpaket",
      fr: "Suite d'ingénierie industrielle",
      pt: "Pacote de engenharia industrial"
    }),
    h2: pick(lang, {
      en: "Wire Mesh Calculation Engineering Tools",
      zh: "金属丝网工程计算工具",
      es: "Herramientas de ingeniería para cálculo de mallas metálicas",
      de: "Drahtgewebe-Berechnungswerkzeuge für Ingenieure",
      fr: "Outils de calcul d'ingénierie pour toiles métalliques",
      pt: "Ferramentas de engenharia para cálculo de telas metálicas"
    }),
    lead: pick(lang, {
      en: "Precise technical calculators for square woven wire mesh aperture, open area percentage, mesh count conversions, unit weight, and fluid flow parameters according to ASTM E2016 and ISO 9044 standards.",
      zh: "遵循 ASTM E2016 与 ISO 9044 标准，提供方孔编织网孔径、开孔率、目数换算、单位重量及流体流动参数的精密工程计算工具。",
      es: "Calculadoras técnicas precisas para abertura de malla cuadrada tejida, porcentaje de área abierta, conversiones de conteo de malla, peso unitario y parámetros de flujo según normas ASTM E2016 e ISO 9044.",
      de: "Präzise technische Rechner für Maschenweite von Quadratmaschengeweben, freie Siebfläche, Maschenzahlumrechnungen, Flächengewicht und Strömungsparameter nach ASTM E2016 und ISO 9044.",
      fr: "Calculateurs techniques précis pour l'ouverture des toiles tissées à mailles carrées, le pourcentage de vide, les conversions de mailles, la masse surfacique et les paramètres d'écoulement selon ASTM E2016 et ISO 9044.",
      pt: "Calculadoras técnicas precisas para abertura de malha quadrada tecida, percentual de área aberta, conversões de contagem de malha, peso unitário e parâmetros de vazão conforme normas ASTM E2016 e ISO 9044."
    }),
    tabGeometry: pick(lang, {
      en: "Open Area & Geometry",
      zh: "开孔率与几何尺寸",
      es: "Área abierta y geometría",
      de: "Freie Siebfläche & Geometrie",
      fr: "Surface ouverte et géométrie",
      pt: "Área aberta e geometria"
    }),
    tabWeight: pick(lang, {
      en: "Mesh Mass & Weight",
      zh: "丝网质量与重量",
      es: "Masa y peso de malla",
      de: "Gewebe-Masse & Gewicht",
      fr: "Masse et poids de toile",
      pt: "Massa e peso da malha"
    }),
    tabFlow: pick(lang, {
      en: "Micron & Flow Speed",
      zh: "微米精度与流速",
      es: "Micras y velocidad de flujo",
      de: "Mikron & Strömungsgeschwindigkeit",
      fr: "Microns et vitesse de flux",
      pt: "Mícrons e velocidade de vazão"
    }),
    card1Title: pick(lang, {
      en: "Open Area & Mesh Count Converter",
      zh: "开孔率与目数换算器",
      es: "Conversor de área abierta y número de mallas",
      de: "Umrechner für freie Siebfläche und Maschenzahl",
      fr: "Convertisseur de surface ouverte et nombre de mailles",
      pt: "Conversor de área aberta e contagem de malha"
    }),
    card1Desc: pick(lang, {
      en: "Calculate aperture opening size, micron rating, and open area percentage based on mesh count and wire diameter.",
      zh: "根据目数与丝径计算网孔净开口尺寸、微米截留等级及开孔率百分比。",
      es: "Calcule el tamaño de abertura, la clasificación en micras y el porcentaje de área abierta según el número de mallas y el diámetro del alambre.",
      de: "Berechnen Sie Maschenweite, Mikron-Abscheidegrad und freie Siebfläche basierend auf Maschenzahl und Drahtdurchmesser.",
      fr: "Calculez l'ouverture de maille, le seuil en microns et le pourcentage de vide à partir du nombre de mailles et du diamètre du fil.",
      pt: "Calcule o tamanho de abertura, retenção em mícrons e percentual de área aberta com base na contagem de malha e no diâmetro do arame."
    }),
    inputPrimaryParam: pick(lang, {
      en: "Input Primary Parameter:",
      zh: "选择主要输入参数：",
      es: "Parámetro principal de entrada:",
      de: "Primären Eingabeparameter wählen:",
      fr: "Paramètre principal d'entrée :",
      pt: "Parâmetro principal de entrada:"
    }),
    modeMesh: pick(lang, {
      en: "Mesh Count + Wire Diameter",
      zh: "目数 + 丝径",
      es: "Número de mallas + Diámetro de alambre",
      de: "Maschenzahl + Drahtdurchmesser",
      fr: "Nombre de mailles + Diamètre du fil",
      pt: "Contagem de malha + Diâmetro do arame"
    }),
    modeAperture: pick(lang, {
      en: "Aperture (mm) + Wire Diameter",
      zh: "孔径 (mm) + 丝径",
      es: "Abertura (mm) + Diámetro de alambre",
      de: "Maschenweite (mm) + Drahtdurchmesser",
      fr: "Ouverture (mm) + Diamètre du fil",
      pt: "Abertura (mm) + Diâmetro do arame"
    }),
    labelMeshCount: pick(lang, {
      en: "Mesh Count (US / Imperial)",
      zh: "目数 (美标 / 英制)",
      es: "Número de mallas (US / Imperial)",
      de: "Maschenzahl (US / Imperial)",
      fr: "Nombre de mailles (US / Impérial)",
      pt: "Contagem de malha (US / Imperial)"
    }),
    wiresPerInch: pick(lang, {
      en: "wires/inch",
      zh: "丝数/英寸",
      es: "hilos/pulgada",
      de: "Drähte/Zoll",
      fr: "fils/pouce",
      pt: "fios/polegada"
    }),
    labelAperture: pick(lang, {
      en: "Aperture Opening (w)",
      zh: "网孔净开口 (w)",
      es: "Abertura de malla (w)",
      de: "Maschenweite (w)",
      fr: "Ouverture de maille (w)",
      pt: "Abertura de malha (w)"
    }),
    labelWireDia: pick(lang, {
      en: "Wire Diameter (d)",
      zh: "丝径 (d)",
      es: "Diámetro del alambre (d)",
      de: "Drahtdurchmesser (d)",
      fr: "Diamètre du fil (d)",
      pt: "Diâmetro do arame (d)"
    }),
    calcSpecs: pick(lang, {
      en: "Calculated Specifications",
      zh: "计算结果规格",
      es: "Especificaciones calculadas",
      de: "Berechnete Spezifikationen",
      fr: "Spécifications calculées",
      pt: "Especificações calculadas"
    }),
    verifiedFormula: pick(lang, {
      en: "ISO 9044 Verified Formula",
      zh: "符合 ISO 9044 标准算法",
      es: "Fórmula verificada ISO 9044",
      de: "ISO 9044 verifizierte Formel",
      fr: "Formule vérifiée ISO 9044",
      pt: "Fórmula verificada ISO 9044"
    }),
    openAreaPct: pick(lang, {
      en: "Open Area (%)",
      zh: "开孔率 (%)",
      es: "Área abierta (%)",
      de: "Freie Siebfläche (%)",
      fr: "Surface ouverte (%)",
      pt: "Área aberta (%)"
    }),
    micronRating: pick(lang, {
      en: "Micron Rating",
      zh: "微米精度等级",
      es: "Clasificación en micras",
      de: "Mikron-Abscheidegrad",
      fr: "Seuil en microns",
      pt: "Classificação em mícrons"
    }),
    apertureSize: pick(lang, {
      en: "Aperture Size (w)",
      zh: "孔径尺寸 (w)",
      es: "Tamaño de abertura (w)",
      de: "Maschenweite (w)",
      fr: "Dimension d'ouverture (w)",
      pt: "Tamanho da abertura (w)"
    }),
    calcMesh: pick(lang, {
      en: "Calculated Mesh",
      zh: "计算目数",
      es: "Malla calculada",
      de: "Berechnete Maschenzahl",
      fr: "Maille calculée",
      pt: "Malha calculada"
    }),
    openRatioViz: pick(lang, {
      en: "Open Area Ratio Visualization",
      zh: "开孔面积比例示意",
      es: "Visualización de proporción de área abierta",
      de: "Visualisierung des freien Siebflächenanteils",
      fr: "Visualisation du ratio de surface ouverte",
      pt: "Visualização da proporção de área aberta"
    }),
    openSolidText: (open: number, solid: number) => pick(lang, {
      en: `${open}% Open / ${solid}% Solid`,
      zh: `${open}% 开孔 / ${solid}% 实体`,
      es: `${open}% Abierto / ${solid}% Sólido`,
      de: `${open}% Offen / ${solid}% Fest`,
      fr: `${open}% Ouvert / ${solid}% Plein`,
      pt: `${open}% Aberto / ${solid}% Sólido`
    }),
    resetTitle: pick(lang, {
      en: "Reset values",
      zh: "重置数值",
      es: "Restablecer valores",
      de: "Werte zurücksetzen",
      fr: "Réinitialiser les valeurs",
      pt: "Redefinir valores"
    }),
    card2Title: pick(lang, {
      en: "Mesh Unit Weight & Sheet Mass Calculator",
      zh: "丝网单位重量与整卷质量计算器",
      es: "Calculadora de peso unitario y masa de lámina/rollo",
      de: "Rechner für Flächengewicht und Rollenmasse",
      fr: "Calculateur de masse surfacique et masse totale de toile",
      pt: "Calculadora de peso unitário e massa da tela/rolo"
    }),
    card2Desc: pick(lang, {
      en: "Determine weight per square meter and total roll/sheet mass based on alloy wire density and physical dimensions.",
      zh: "基于合金丝密度与实际几何尺寸，计算每平方米重量及整卷/板材的总质量。",
      es: "Determine el peso por metro cuadrado y la masa total del rollo o lámina según la densidad del alambre y las dimensiones.",
      de: "Ermitteln Sie das Quadratmetergewicht und die Gesamtmasse der Rolle/Platte anhand der Legierungsdichte und Abmessungen.",
      fr: "Déterminez la masse au mètre carré et la masse totale du rouleau/panneau selon la densité de l'alliage et les dimensions.",
      pt: "Determine o peso por metro quadrado e a massa total do rolo/chapa com base na densidade da liga e nas dimensões."
    }),
    alloySelect: pick(lang, {
      en: "Alloy / Material Selection",
      zh: "合金 / 材质选择",
      es: "Selección de aleación / material",
      de: "Legierungs- / Werkstoffauswahl",
      fr: "Sélection de l'alliage / matériau",
      pt: "Seleção de liga / material"
    }),
    lengthM: pick(lang, {
      en: "Length (m)",
      zh: "长度 (m)",
      es: "Longitud (m)",
      de: "Länge (m)",
      fr: "Longueur (m)",
      pt: "Comprimento (m)"
    }),
    widthM: pick(lang, {
      en: "Width (m)",
      zh: "宽度 (m)",
      es: "Ancho (m)",
      de: "Breite (m)",
      fr: "Largeur (m)",
      pt: "Largura (m)"
    }),
    estMassSummary: pick(lang, {
      en: "Estimated Mass Summary",
      zh: "估算质量摘要",
      es: "Resumen de masa estimada",
      de: "Zusammenfassung der geschätzten Masse",
      fr: "Résumé de la masse estimée",
      pt: "Resumo da massa estimada"
    }),
    unitWeight: pick(lang, {
      en: "Unit Weight",
      zh: "单位重量",
      es: "Peso unitario",
      de: "Flächengewicht",
      fr: "Masse surfacique",
      pt: "Peso unitário"
    }),
    sheetSurface: pick(lang, {
      en: "Sheet Surface",
      zh: "网面总面积",
      es: "Superficie total",
      de: "Gesamtfläche",
      fr: "Surface totale",
      pt: "Superfície total"
    }),
    totalMassKg: pick(lang, {
      en: "Total Mass (kg)",
      zh: "总重量 (kg)",
      es: "Masa total (kg)",
      de: "Gesamtmasse (kg)",
      fr: "Masse totale (kg)",
      pt: "Massa total (kg)"
    }),
    totalMassLbs: pick(lang, {
      en: "Total Mass (lbs)",
      zh: "总重量 (lbs)",
      es: "Masa total (lbs)",
      de: "Gesamtmasse (lbs)",
      fr: "Masse totale (lbs)",
      pt: "Massa total (lbs)"
    }),
    card3Title: pick(lang, {
      en: "Micron Rating & Flow Velocity Estimator",
      zh: "微米精度与穿流迎面流速估算器",
      es: "Estimador de micras y velocidad de flujo",
      de: "Schätzer für Mikron-Abscheidegrad & Strömungsgeschwindigkeit",
      fr: "Estimateur de seuil en microns et vitesse d'écoulement",
      pt: "Estimador de mícrons e velocidade de vazão"
    }),
    card3Desc: pick(lang, {
      en: "Estimate filter surface velocity and mesh pore velocity based on nominal micron retention and flow rate parameters.",
      zh: "根据公称过滤微米精度与流量参数，估算过滤表面迎面流速与网孔通透流速。",
      es: "Estime la velocidad superficial del filtro y la velocidad en los poros según la retención nominal en micras y el caudal.",
      de: "Schätzen Sie Filteranströmgeschwindigkeit und Porenströmungsgeschwindigkeit anhand von Mikron-Rückhaltung und Durchfluss.",
      fr: "Estimez la vitesse frontale et la vitesse dans les pores à partir du seuil nominal en microns et du débit.",
      pt: "Estime a velocidade superficial do filtro e a velocidade nos poros com base na retenção nominal em mícrons e na vazão."
    }),
    nominalMicron: pick(lang, {
      en: "Nominal Filtration Micron (µm)",
      zh: "公称过滤微米精度 (µm)",
      es: "Micras nominales de filtración (µm)",
      de: "Nominale Filtration (µm)",
      fr: "Filtration nominale en microns (µm)",
      pt: "Filtração nominal em mícrons (µm)"
    }),
    volFlowRate: pick(lang, {
      en: "Volumetric Flow Rate (m³/h)",
      zh: "体积流量 (m³/h)",
      es: "Caudal volumétrico (m³/h)",
      de: "Volumenstrom (m³/h)",
      fr: "Débit volumique (m³/h)",
      pt: "Vazão volumétrica (m³/h)"
    }),
    effectiveArea: pick(lang, {
      en: "Effective Filter Area (m²)",
      zh: "有效过滤面积 (m²)",
      es: "Área efectiva de filtración (m²)",
      de: "Effektive Filterfläche (m²)",
      fr: "Surface filtrante efficace (m²)",
      pt: "Área efetiva de filtração (m²)"
    }),
    fluidParams: pick(lang, {
      en: "Fluid Dynamics Parameters",
      zh: "流体动力学估算参数",
      es: "Parámetros de dinámica de fluidos",
      de: "Strömungsmechanische Parameter",
      fr: "Paramètres de dynamique des fluides",
      pt: "Parâmetros de dinâmica de fluidos"
    }),
    estimateBadge: pick(lang, {
      en: "Estimate",
      zh: "估算参考",
      es: "Estimación",
      de: "Schätzwert",
      fr: "Estimation",
      pt: "Estimativa"
    }),
    equivMesh: pick(lang, {
      en: "Equivalent Mesh Spec",
      zh: "等效目数参考",
      es: "Malla equivalente aprox.",
      de: "Äquivalente Maschenzahl",
      fr: "Maille équivalente approx.",
      pt: "Malha equivalente aprox."
    }),
    linearCountNote: pick(lang, {
      en: "Approximate linear count",
      zh: "近似线性网目计数",
      es: "Conteo lineal aproximado",
      de: "Ungefähre lineare Maschenzahl",
      fr: "Nombre linéaire approximatif",
      pt: "Contagem linear aproximada"
    }),
    approachVelocity: pick(lang, {
      en: "Approach Velocity",
      zh: "迎面流速",
      es: "Velocidad de aproximación",
      de: "Anströmgeschwindigkeit",
      fr: "Vitesse d'approche",
      pt: "Velocidade de aproximação"
    }),
    approachNote: pick(lang, {
      en: "Gross filter face speed",
      zh: "整体滤网表面迎面流速",
      es: "Velocidad frontal bruta",
      de: "Brutto-Filterflächengeschwindigkeit",
      fr: "Vitesse brute sur face filtrante",
      pt: "Velocidade frontal bruta"
    }),
    poreVelocity: pick(lang, {
      en: "Estimated Pore Velocity",
      zh: "网孔间隙估算流速",
      es: "Velocidad estimada en poros",
      de: "Geschätzte Porengeschwindigkeit",
      fr: "Vitesse estimée dans les pores",
      pt: "Velocidade estimada nos poros"
    }),
    poreNote: pick(lang, {
      en: "Interstitially through open apertures",
      zh: "流体实际穿过开孔通道速度",
      es: "Intersticial a través de aberturas",
      de: "Interstitiell durch freie Poren",
      fr: "Interstitielle à travers les mailles ouvertes",
      pt: "Intersticial através das aberturas abertas"
    }),
    refTableTitle: pick(lang, {
      en: "Standard Industrial Mesh Reference Table",
      zh: "工业标准编织丝网常用参考表",
      es: "Tabla de referencia de malla industrial estándar",
      de: "Referenztabelle für Standard-Industriedrahtgewebe",
      fr: "Tableau de référence des toiles industrielles standard",
      pt: "Tabela de referência de telas industriais padrão"
    }),
    refTableDesc: pick(lang, {
      en: "ASTM E2016 verified values for Plain Weave Stainless Steel Wire Mesh",
      zh: "平纹编织不锈钢丝网 ASTM E2016 标准参考数值",
      es: "Valores verificados según ASTM E2016 para malla tejida lisa de acero inoxidable",
      de: "Verifizierte Werte nach ASTM E2016 für Edelstahlgewebe in Leinwandbindung",
      fr: "Valeurs vérifiées selon ASTM E2016 pour toile métallique unie en acier inoxydable",
      pt: "Valores verificados conforme ASTM E2016 para malha de aço inoxidável em trama lisa"
    }),
    viewFullSpecs: pick(lang, {
      en: "View Full Specs",
      zh: "查看完整规格",
      es: "Ver especificaciones completas",
      de: "Vollständige Daten anzeigen",
      fr: "Voir toutes les spécifications",
      pt: "Ver especificações completas"
    }),
    thMesh: pick(lang, {
      en: "Mesh Count",
      zh: "目数",
      es: "Número de mallas",
      de: "Maschenzahl",
      fr: "Nombre de mailles",
      pt: "Contagem de malha"
    }),
    thWire: pick(lang, {
      en: "Wire Dia (mm)",
      zh: "丝径 (mm)",
      es: "Diám. alambre (mm)",
      de: "Draht-Ø (mm)",
      fr: "Diam. fil (mm)",
      pt: "Diâm. arame (mm)"
    }),
    thAperture: pick(lang, {
      en: "Aperture (mm)",
      zh: "孔径 (mm)",
      es: "Abertura (mm)",
      de: "Maschenweite (mm)",
      fr: "Ouverture (mm)",
      pt: "Abertura (mm)"
    }),
    thMicron: pick(lang, {
      en: "Microns (µm)",
      zh: "微米 (µm)",
      es: "Micras (µm)",
      de: "Mikron (µm)",
      fr: "Microns (µm)",
      pt: "Mícrons (µm)"
    }),
    thOpenArea: pick(lang, {
      en: "Open Area (%)",
      zh: "开孔率 (%)",
      es: "Área abierta (%)",
      de: "Freie Siebfläche (%)",
      fr: "Surface ouverte (%)",
      pt: "Área aberta (%)"
    }),
    thMass: pick(lang, {
      en: "Mass (kg/m²)",
      zh: "质量 (kg/m²)",
      es: "Masa (kg/m²)",
      de: "Masse (kg/m²)",
      fr: "Masse (kg/m²)",
      pt: "Massa (kg/m²)"
    }),
    calcEquations: pick(lang, {
      en: "Calculation Equations",
      zh: "计算公式解析",
      es: "Ecuaciones de cálculo",
      de: "Berechnungsgleichungen",
      fr: "Équations de calcul",
      pt: "Equações de cálculo"
    }),
    eq1Title: pick(lang, {
      en: "1. Open Area Percentage (% OA)",
      zh: "1. 开孔率百分比 (% OA)",
      es: "1. Porcentaje de área abierta (% OA)",
      de: "1. Freie Siebfläche (% OA)",
      fr: "1. Pourcentage de vide (% OA)",
      pt: "1. Percentual de área aberta (% OA)"
    }),
    eq1Desc: pick(lang, {
      en: "Where w = aperture size (mm) and d = wire diameter (mm).",
      zh: "式中 w 为网孔开口净尺寸 (mm)，d 为编织丝径 (mm)。",
      es: "Donde w = tamaño de abertura (mm) y d = diámetro del alambre (mm).",
      de: "Wobei w = Maschenweite (mm) und d = Drahtdurchmesser (mm).",
      fr: "Où w = ouverture de maille (mm) et d = diamètre du fil (mm).",
      pt: "Onde w = tamanho da abertura (mm) e d = diâmetro do arame (mm)."
    }),
    eq2Title: pick(lang, {
      en: "2. Mesh Count Relationship",
      zh: "2. 目数与孔距几何关系",
      es: "2. Relación de número de mallas",
      de: "2. Maschenzahl-Beziehung",
      fr: "2. Relation du nombre de mailles",
      pt: "2. Relação de contagem de malha"
    }),
    eq2Desc: pick(lang, {
      en: "Linear apertures per inch (25.4 mm).",
      zh: "每线性英寸 (25.4 mm) 内的孔洞数量。",
      es: "Aberturas lineales por pulgada (25.4 mm).",
      de: "Lineare Öffnungen pro Zoll (25,4 mm).",
      fr: "Nombre d'ouvertures linéaires par pouce (25,4 mm).",
      pt: "Aberturas lineares por polegada (25,4 mm)."
    }),
    eq3Title: pick(lang, {
      en: "3. Unit Mass Weight Formula",
      zh: "3. 单位米重质量公式",
      es: "3. Fórmula de masa unitaria",
      de: "3. Formel für Flächengewicht",
      fr: "3. Formule de masse surfacique",
      pt: "3. Fórmula de massa unitária"
    }),
    eq3Desc: pick(lang, {
      en: "Adjusted for alloy mass density ρ in g/cm³.",
      zh: "根据不同金属合金实际密度 ρ (g/cm³) 进行换算修正。",
      es: "Ajustado según la densidad de la aleación ρ en g/cm³.",
      de: "Angepasst an die Legierungsdichte ρ in g/cm³.",
      fr: "Ajusté selon la masse volumique de l'alliage ρ en g/cm³.",
      pt: "Ajustado para a densidade da liga ρ em g/cm³."
    }),
    compStandards: pick(lang, {
      en: "Compliance Standards",
      zh: "参考工程标准",
      es: "Normas de referencia",
      de: "Referenznormen",
      fr: "Normes de référence",
      pt: "Normas de referência"
    }),
    customOemBadge: pick(lang, {
      en: "Custom OEM Engineering",
      zh: "定制 OEM 工程支持",
      es: "Ingeniería OEM a medida",
      de: "Kundenspezifische OEM-Technik",
      fr: "Ingénierie OEM sur mesure",
      pt: "Engenharia OEM sob medida"
    }),
    customOemTitle: pick(lang, {
      en: "Need Custom Specification Verification?",
      zh: "需要定制规格参数校验？",
      es: "¿Requiere verificación de especificaciones a medida?",
      de: "Benötigen Sie individuelle Spezifikationsprüfung?",
      fr: "Besoin de vérifier des spécifications sur mesure ?",
      pt: "Precisa de validação de especificações sob medida?"
    }),
    customOemDesc: pick(lang, {
      en: "Our filtration engineers perform exact technical validation for non-standard weave geometries, sintered multilayer structures, and high-pressure media packs.",
      zh: "我们的过滤技术团队可为非标编织几何形态、烧结多层结构及高压滤材包提供严谨的技术参数核算与制造公差评估。",
      es: "Nuestros ingenieros de filtración realizan validaciones técnicas precisas para tejidos no estándar, estructuras multicapa sinterizadas y paquetes de medios de alta presión.",
      de: "Unsere Filtrationsingenieure führen genaue technische Überprüfungen für Sondergewebe, mehrlagige Sinterstrukturen und Hochdruck-Medienpakete durch.",
      fr: "Nos ingénieurs en filtration effectuent une validation technique précise pour les géométries de tissage hors norme, composites frittés et packs de filtration haute pression.",
      pt: "Nossos engenheiros de filtração executam validação técnica precisa para tecelagens especiais, estruturas sinterizadas multicamadas e elementos sob alta pressão."
    }),
    reqQuoteBtn: pick(lang, {
      en: "Request Custom Quote",
      zh: "获取工程报价",
      es: "Solicitar cotización personalizada",
      de: "Individuelles Angebot anfordern",
      fr: "Demander un devis sur mesure",
      pt: "Solicitar cotação personalizada"
    }),
    datasheetTitle: pick(lang, {
      en: "Technical Datasheet Access",
      zh: "技术数据表下载",
      es: "Acceso a hojas técnicas",
      de: "Zugriff auf technische Datenblätter",
      fr: "Accès aux fiches techniques",
      pt: "Acesso a fichas técnicas"
    }),
    datasheetDesc: pick(lang, {
      en: "Download full ISO standards matrices, corrosion guides, and weave tolerance tables in our resource center.",
      zh: "在资源中心下载完整 ISO 标准参数表、耐腐蚀指南与编织公差对照表。",
      es: "Descargue matrices de normas ISO completas, guías de corrosión y tablas de tolerancias en nuestro centro de recursos.",
      de: "Laden Sie vollständige ISO-Matrizen, Korrosionsleitfäden und Toleranztabellen in unserem Ressourcenzentrum herunter.",
      fr: "Téléchargez les matrices de normes ISO complètes, guides de corrosion et tolérances dans notre centre de ressources.",
      pt: "Baixe tabelas completas de normas ISO, guias de corrosão e tolerâncias em nosso centro de recursos."
    }),
    browseDatasheets: pick(lang, {
      en: "Browse Datasheets",
      zh: "浏览数据表",
      es: "Explorar hojas técnicas",
      de: "Datenblätter durchsuchen",
      fr: "Consulter les fiches",
      pt: "Ver fichas técnicas"
    })
  };

  // Computed Values for Tab 1
  const geometryResults = useMemo(() => {
    let mesh = meshInput;
    let wire = wireMmInput;
    let aperture = apertureInput;
    if (calcMode === "from_mesh") {
      const pitch = mesh > 0 ? 25.4 / mesh : 0;
      aperture = Math.max(0, pitch - wire);
    } else {
      const pitch = aperture + wire;
      mesh = pitch > 0 ? 25.4 / pitch : 0;
    }
    const pitch = aperture + wire;
    const openArea = pitch > 0 ? Math.pow(aperture / pitch, 2) * 100 : 0;
    const micron = aperture * 1000;
    const wiresPerCm = mesh > 0 ? mesh / 2.54 : 0;
    return {
      mesh: Number(mesh.toFixed(2)),
      wireMm: Number(wire.toFixed(4)),
      apertureMm: Number(aperture.toFixed(4)),
      micron: Number(micron.toFixed(1)),
      openArea: Number(openArea.toFixed(2)),
      wiresPerCm: Number(wiresPerCm.toFixed(1)),
      pitchMm: Number(pitch.toFixed(4))
    };
  }, [calcMode, meshInput, wireMmInput, apertureInput]);

  // Computed Values for Tab 2
  const weightResults = useMemo(() => {
    const mat = MATERIAL_DENSITIES[materialKey] || MATERIAL_DENSITIES.ss316;
    const densityRatio = mat.density / 7.93;
    const weightPerSqM = 0.5 * weightMesh * Math.pow(weightWireMm, 2) * densityRatio;
    const totalAreaSqM = sheetLength * sheetWidth;
    const totalWeightKg = weightPerSqM * totalAreaSqM;
    const totalWeightLbs = totalWeightKg * 2.20462;
    return {
      weightPerSqM: Number(weightPerSqM.toFixed(3)),
      totalAreaSqM: Number(totalAreaSqM.toFixed(2)),
      totalWeightKg: Number(totalWeightKg.toFixed(3)),
      totalWeightLbs: Number(totalWeightLbs.toFixed(3)),
      materialName: materialNames[materialKey] || materialNames.ss316
    };
  }, [weightMesh, weightWireMm, materialKey, sheetLength, sheetWidth, materialNames]);

  // Computed Values for Tab 3
  const flowResults = useMemo(() => {
    const estMesh = micronInput > 0 ? Math.round(15200 / micronInput) : 0;
    const velocityMps = filterArea > 0 ? flowRate / (3600 * filterArea) : 0;
    const approxOpenArea = 35; // typical average open area %
    const effectiveArea = filterArea * (approxOpenArea / 100);
    const poreVelocity = effectiveArea > 0 ? flowRate / (3600 * effectiveArea) : 0;
    return {
      estMesh,
      velocityMps: Number(velocityMps.toFixed(3)),
      poreVelocity: Number(poreVelocity.toFixed(3))
    };
  }, [micronInput, flowRate, filterArea]);
  const handleResetGeometry = () => {
    setMeshInput(80);
    setWireMmInput(0.12);
    setApertureInput(0.198);
    setCalcMode("from_mesh");
  };
  const handleResetWeight = () => {
    setWeightMesh(100);
    setWeightWireMm(0.10);
    setMaterialKey("ss316");
    setSheetLength(1.0);
    setSheetWidth(1.0);
  };
  const referenceColumns: DataTableColumn<ReferenceMeshRow>[] = useMemo(() => [{
    key: "mesh",
    header: copy.thMesh,
    cell: row => `${row.mesh} Mesh`,
    sticky: true,
    mobileTitle: true
  }, {
    key: "wireMm",
    header: copy.thWire,
    align: "right",
    cell: row => row.wireMm.toFixed(3)
  }, {
    key: "apertureMm",
    header: copy.thAperture,
    align: "right",
    cell: row => row.apertureMm.toFixed(3)
  }, {
    key: "micron",
    header: copy.thMicron,
    align: "right",
    cell: row => <span className="font-mono text-foreground font-medium">{row.micron}</span>
  }, {
    key: "openArea",
    header: copy.thOpenArea,
    align: "right",
    emphasis: true,
    cell: row => <span className="text-primary font-semibold">{row.openArea}%</span>
  }, {
    key: "weightKg",
    header: copy.thMass,
    align: "right",
    cell: row => row.weightKg.toFixed(2)
  }], [copy.thMesh, copy.thWire, copy.thAperture, copy.thMicron, copy.thOpenArea, copy.thMass]);
  return <section id="calculations" data-nav="light" className="bg-background text-foreground mf-section">
      <div className="mf-container">
        
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl mb-12">
            <Badge variant="outline" className="mf-caption font-mono uppercase tracking-wider mb-4 border-border bg-card text-foreground gap-1.5">
              <Calculator className="size-3.5 text-primary" aria-hidden="true" />
              <span>{copy.badge}</span>
            </Badge>
            <h2 className="mf-h2 text-foreground font-default">
              {copy.h2}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground max-w-[65ch]">
              {copy.lead}
            </p>
          </div>
        </Reveal>

        {/* Main Grid: Interactive Calculator Workspace */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Main Interactive Panel (Left - 8 cols) */}
          <div className="lg:col-span-8 space-y-6">
            <Tabs defaultValue="geometry" className="w-full">
              <TabsList className="grid grid-cols-3 w-full bg-muted p-1 rounded-lg border border-border">
                <TabsTrigger value="geometry" className="flex items-center gap-2 text-sm font-medium">
                  <Ruler className="size-4 shrink-0" />
                  <span>{copy.tabGeometry}</span>
                </TabsTrigger>
                <TabsTrigger value="weight" className="flex items-center gap-2 text-sm font-medium">
                  <Scale className="size-4 shrink-0" />
                  <span>{copy.tabWeight}</span>
                </TabsTrigger>
                <TabsTrigger value="flow" className="flex items-center gap-2 text-sm font-medium">
                  <Activity className="size-4 shrink-0" />
                  <span>{copy.tabFlow}</span>
                </TabsTrigger>
              </TabsList>

              {/* TAB 1: GEOMETRY & OPEN AREA */}
              <TabsContent value="geometry" className="mt-6 space-y-6 focus-visible:outline-none">
                <Card className="border border-border bg-card">
                  <CardHeader className="border-b border-border bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-semibold text-foreground">{copy.card1Title}</CardTitle>
                        <CardDescription className="mf-caption text-muted-foreground">
                          {copy.card1Desc}
                        </CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleResetGeometry} title={copy.resetTitle}>
                        <RotateCcw className="size-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    
                    {/* Calculation Mode Toggle */}
                    <fieldset className="flex flex-wrap gap-4 items-center bg-muted/40 p-3 rounded-md border border-border">
                      <legend className="text-sm font-semibold uppercase tracking-wider text-muted-foreground px-1">{copy.inputPrimaryParam}</legend>
                      <div className="flex items-center gap-4 text-sm">
                        <label className="flex items-center gap-2 cursor-pointer font-medium text-foreground min-h-[44px]">
                          <input type="radio" name="calcMode" checked={calcMode === "from_mesh"} onChange={() => setCalcMode("from_mesh")} className="accent-primary text-primary focus:ring-ring size-4" />
                          <span>{copy.modeMesh}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer font-medium text-foreground min-h-[44px]">
                          <input type="radio" name="calcMode" checked={calcMode === "from_aperture"} onChange={() => setCalcMode("from_aperture")} className="accent-primary text-primary focus:ring-ring size-4" />
                          <span>{copy.modeAperture}</span>
                        </label>
                      </div>
                    </fieldset>

                    {/* Input Controls */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {calcMode === "from_mesh" ? <div className="space-y-2">
                          <Label htmlFor="geom-mesh-input" className="text-sm font-medium text-foreground flex items-center justify-between">
                            <span>{copy.labelMeshCount}</span>
                            <span className="text-sm text-muted-foreground">{copy.wiresPerInch}</span>
                          </Label>
                          <Input id="geom-mesh-input" type="number" min="1" max="635" step="1" value={meshInput} onChange={e => setMeshInput(Math.max(1, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                        </div> : <div className="space-y-2">
                          <Label htmlFor="geom-aperture-input" className="text-sm font-medium text-foreground flex items-center justify-between">
                            <span>{copy.labelAperture}</span>
                            <span className="text-sm text-muted-foreground">mm</span>
                          </Label>
                          <Input id="geom-aperture-input" type="number" min="0.01" max="25.0" step="0.001" value={apertureInput} onChange={e => setApertureInput(Math.max(0.001, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                        </div>}

                      <div className="space-y-2">
                        <Label htmlFor="geom-wire-input" className="text-sm font-medium text-foreground flex items-center justify-between">
                          <span>{copy.labelWireDia}</span>
                          <span className="text-sm text-muted-foreground">mm</span>
                        </Label>
                        <Input id="geom-wire-input" type="number" min="0.01" max="5.0" step="0.005" value={wireMmInput} onChange={e => setWireMmInput(Math.max(0.005, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                      </div>
                    </div>

                    {/* Calculated Output Display Box */}
                    <div aria-live="polite" className="bg-muted/50 rounded-lg p-6 border border-border space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{copy.calcSpecs}</span>
                        <Badge variant="outline" className="bg-background text-foreground border-border">{copy.verifiedFormula}</Badge>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2">
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.openAreaPct}</p>
                          <p className="text-2xl font-bold text-primary mt-1">{geometryResults.openArea}%</p>
                        </div>
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.micronRating}</p>
                          <p className="text-2xl font-bold text-foreground mt-1">{geometryResults.micron} µm</p>
                        </div>
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.apertureSize}</p>
                          <p className="text-lg font-bold text-foreground mt-1.5">{geometryResults.apertureMm} mm</p>
                        </div>
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.calcMesh}</p>
                          <p className="text-lg font-bold text-foreground mt-1.5">{geometryResults.mesh} Mesh</p>
                        </div>
                      </div>

                      {/* Visual Open Area Bar */}
                      <div className="space-y-1.5 pt-2">
                        <div className="flex justify-between mf-caption text-muted-foreground">
                          <span>{copy.openRatioViz}</span>
                          <span>{copy.openSolidText(geometryResults.openArea, Number((100 - geometryResults.openArea).toFixed(2)))}</span>
                        </div>
                        <div className="h-3 w-full bg-muted rounded-full overflow-hidden border border-border">
                          <div className="h-full bg-primary transition-all duration-300" style={{
                          width: `${Math.min(100, Math.max(0, geometryResults.openArea))}%`
                        }} />
                        </div>
                      </div>
                    </div>

                  </CardContent>
                </Card>
              </TabsContent>

              {/* TAB 2: MESH WEIGHT CALCULATOR */}
              <TabsContent value="weight" className="mt-6 space-y-6 focus-visible:outline-none">
                <Card className="border border-border bg-card">
                  <CardHeader className="border-b border-border bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-semibold text-foreground">{copy.card2Title}</CardTitle>
                        <CardDescription className="mf-caption text-muted-foreground">
                          {copy.card2Desc}
                        </CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleResetWeight} title={copy.resetTitle}>
                        <RotateCcw className="size-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="weight-alloy-select" className="text-sm font-medium text-foreground">{copy.alloySelect}</Label>
                        <Select value={materialKey} onValueChange={setMaterialKey}>
                          <SelectTrigger id="weight-alloy-select" className="w-full bg-background border-input">
                            <SelectValue placeholder="Select alloy density" />
                          </SelectTrigger>
                          <SelectContent className="bg-popover text-popover-foreground border-border">
                            {Object.entries(materialNames).map(([key, label]) => <SelectItem key={key} value={key}>
                                {label}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="weight-mesh-input" className="text-sm font-medium text-foreground">{copy.labelMeshCount}</Label>
                        <Input id="weight-mesh-input" type="number" min="1" max="635" value={weightMesh} onChange={e => setWeightMesh(Math.max(1, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="weight-wire-input" className="text-sm font-medium text-foreground">{copy.labelWireDia}</Label>
                        <Input id="weight-wire-input" type="number" min="0.01" max="5.0" step="0.005" value={weightWireMm} onChange={e => setWeightWireMm(Math.max(0.005, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="weight-sheet-length" className="text-sm font-medium text-foreground">{copy.lengthM}</Label>
                          <Input id="weight-sheet-length" type="number" min="0.1" step="0.1" value={sheetLength} onChange={e => setSheetLength(Math.max(0.1, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="weight-sheet-width" className="text-sm font-medium text-foreground">{copy.widthM}</Label>
                          <Input id="weight-sheet-width" type="number" min="0.1" step="0.1" value={sheetWidth} onChange={e => setSheetWidth(Math.max(0.1, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                        </div>
                      </div>
                    </div>

                    {/* Calculated Weight Results */}
                    <div aria-live="polite" className="bg-muted/50 rounded-lg p-6 border border-border space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{copy.estMassSummary}</span>
                        <span className="text-sm text-muted-foreground font-mono">{weightResults.materialName}</span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.unitWeight}</p>
                          <p className="text-xl font-bold text-primary mt-1">{weightResults.weightPerSqM} <span className="mf-caption font-normal text-muted-foreground">kg/m²</span></p>
                        </div>
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.sheetSurface}</p>
                          <p className="text-xl font-bold text-foreground mt-1">{weightResults.totalAreaSqM} <span className="mf-caption font-normal text-muted-foreground">m²</span></p>
                        </div>
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.totalMassKg}</p>
                          <p className="text-xl font-bold text-foreground mt-1">{weightResults.totalWeightKg} <span className="mf-caption font-normal text-muted-foreground">kg</span></p>
                        </div>
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.totalMassLbs}</p>
                          <p className="text-xl font-bold text-foreground mt-1">{weightResults.totalWeightLbs} <span className="mf-caption font-normal text-muted-foreground">lbs</span></p>
                        </div>
                      </div>
                    </div>

                  </CardContent>
                </Card>
              </TabsContent>

              {/* TAB 3: MICRON & FLOW SPEED */}
              <TabsContent value="flow" className="mt-6 space-y-6 focus-visible:outline-none">
                <Card className="border border-border bg-card">
                  <CardHeader className="border-b border-border bg-muted/30">
                    <CardTitle className="text-lg font-semibold text-foreground">{copy.card3Title}</CardTitle>
                    <CardDescription className="mf-caption text-muted-foreground">
                      {copy.card3Desc}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="flow-micron-input" className="text-sm font-medium text-foreground">{copy.nominalMicron}</Label>
                        <Input id="flow-micron-input" type="number" min="5" max="2000" value={micronInput} onChange={e => setMicronInput(Math.max(1, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="flow-rate-input" className="text-sm font-medium text-foreground">{copy.volFlowRate}</Label>
                        <Input id="flow-rate-input" type="number" min="0.1" step="0.5" value={flowRate} onChange={e => setFlowRate(Math.max(0.1, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="flow-area-input" className="text-sm font-medium text-foreground">{copy.effectiveArea}</Label>
                        <Input id="flow-area-input" type="number" min="0.01" step="0.05" value={filterArea} onChange={e => setFilterArea(Math.max(0.01, parseFloat(e.target.value) || 0))} className="bg-background border-input" />
                      </div>
                    </div>

                    <div aria-live="polite" className="bg-muted/50 rounded-lg p-6 border border-border space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{copy.fluidParams}</span>
                        <Badge variant="outline" className="bg-background text-foreground border-border">{copy.estimateBadge}</Badge>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.equivMesh}</p>
                          <p className="text-xl font-bold text-primary mt-1">~{flowResults.estMesh} Mesh</p>
                          <p className="mf-caption text-muted-foreground mt-1">{copy.linearCountNote}</p>
                        </div>
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.approachVelocity}</p>
                          <p className="text-xl font-bold text-foreground mt-1">{flowResults.velocityMps} m/s</p>
                          <p className="mf-caption text-muted-foreground mt-1">{copy.approachNote}</p>
                        </div>
                        <div className="bg-card p-3 rounded-lg border border-border">
                          <p className="mf-caption text-muted-foreground font-medium">{copy.poreVelocity}</p>
                          <p className="text-xl font-bold text-foreground mt-1">{flowResults.poreVelocity} m/s</p>
                          <p className="mf-caption text-muted-foreground mt-1">{copy.poreNote}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Quick Spec Reference Table */}
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border bg-muted/20 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold text-foreground">{copy.refTableTitle}</CardTitle>
                    <CardDescription className="mf-caption text-muted-foreground">{copy.refTableDesc}</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" asChild className="hidden sm:inline-flex mf-caption">
                    <Link to="/TechnicalChartsPage">
                      {copy.viewFullSpecs} <ArrowRight className="size-3.5 ml-1" aria-hidden="true" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <DataTable columns={referenceColumns} rows={REFERENCE_MESH_DATA} getRowKey={row => row.mesh.toString()} minWidth={640} />
              </CardContent>
            </Card>

          </div>

          {/* Right Sidebar: Formula Reference & Technical RFQ (4 cols) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Engineering Formula Reference Card */}
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border bg-muted/20">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                  <FileText className="size-4" />
                  <span>{copy.calcEquations}</span>
                </div>
              </CardHeader>
              <CardContent className="p-5 space-y-4 mf-caption">
                
                <div className="space-y-1.5 bg-muted/30 p-3 rounded-md border border-border">
                  <p className="font-semibold text-foreground">{copy.eq1Title}</p>
                  <p className="font-mono text-muted-foreground">% OA = [ w / (w + d) ]² × 100</p>
                  <p className="text-muted-foreground mf-caption font-sans">{copy.eq1Desc}</p>
                </div>

                <div className="space-y-1.5 bg-muted/30 p-3 rounded-md border border-border">
                  <p className="font-semibold text-foreground">{copy.eq2Title}</p>
                  <p className="font-mono text-muted-foreground">Mesh = 25.4 / (w + d)</p>
                  <p className="text-muted-foreground mf-caption font-sans">{copy.eq2Desc}</p>
                </div>

                <div className="space-y-1.5 bg-muted/30 p-3 rounded-md border border-border">
                  <p className="font-semibold text-foreground">{copy.eq3Title}</p>
                  <p className="font-mono text-muted-foreground">W (kg/m²) ≈ 0.5 × Mesh × d² × (ρ / 7.93)</p>
                  <p className="text-muted-foreground mf-caption font-sans">{copy.eq3Desc}</p>
                </div>

                <Separator className="bg-border" />

                <div className="space-y-2 pt-1">
                  <p className="font-semibold text-foreground">{copy.compStandards}</p>
                  <ul className="space-y-1 text-muted-foreground mf-caption">
                    <li className="flex items-center gap-1.5">
                      <CircleCheck className="size-4 text-primary shrink-0" aria-hidden="true" /> ASTM E2016 Standard Specification
                    </li>
                    <li className="flex items-center gap-1.5">
                      <CircleCheck className="size-4 text-primary shrink-0" aria-hidden="true" /> ISO 9044 Industrial Wire Cloth
                    </li>
                    <li className="flex items-center gap-1.5">
                      <CircleCheck className="size-4 text-primary shrink-0" aria-hidden="true" /> ISO 4783 Wire Mesh Code Specification
                    </li>
                  </ul>
                </div>

              </CardContent>
            </Card>

            {/* Custom Technical Assistance CTA Card */}
            <Card className="border border-border bg-primary text-primary-foreground">
              <CardContent className="p-6 space-y-4">
                <Badge variant="outline" className="bg-primary-foreground/10 text-primary-foreground border-transparent text-sm">
                  {copy.customOemBadge}
                </Badge>
                <h3 className="mf-h3 text-primary-foreground">
                  {copy.customOemTitle}
                </h3>
                <p className="mf-caption text-primary-foreground/90 leading-relaxed">
                  {copy.customOemDesc}
                </p>
                <div className="pt-2">
                  <Button asChild size="default" variant="secondary" className="w-full font-medium">
                    <Link to="/RequestAQuotePage">
                      {copy.reqQuoteBtn} <ArrowRight className="size-4 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Downloadable Calculation PDF Info */}
            <div className="p-4 rounded-lg border border-border bg-card flex items-start gap-3">
              <Info className="size-5 text-primary shrink-0 mt-0.5" />
              <div className="mf-caption space-y-1">
                <p className="font-semibold text-foreground">{copy.datasheetTitle}</p>
                <p className="text-muted-foreground">{copy.datasheetDesc}</p>
                <Link to="/DatasheetsPage" className="inline-flex items-center gap-1 text-primary font-medium hover:underline pt-1">
                  {copy.browseDatasheets} <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>;
}